import React, { useEffect, useState, useCallback } from "react";
import { FaBriefcase, FaFileAlt, FaMoneyBillWave } from "react-icons/fa";
import {
  getUserDocsUpload,
  getUserIncomeSources,
  getUserWorkExperience,
} from "../../actions/user";
import Pagination from "../Pagination/Pagination";

// ---------- small helpers ----------
const formatDate = (value) => {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return value;
  }
};

const titleCaseFromSnake = (str = "") =>
  str.split("_").map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(" ");


const normalizePaginated = (payload) => {
  if (!payload) return { items: [], current: 1, total: 1, hasPagination: false };
  
  if (Array.isArray(payload.data) && (payload.current_page || payload.last_page)) {
    return {
      items: payload.data,
      current: Number(payload.current_page || 1),
      total: Number(payload.last_page || 1),
      hasPagination: true,
    };
  }
  
  if (Array.isArray(payload)) {
    return { items: payload, current: 1, total: 1, hasPagination: false };
  }
  
  return { items: [], current: 1, total: 1, hasPagination: false };
};

// ---------- UI pieces ----------
const Section = ({ title, icon, items, renderItem, footer }) => (
  <section className="mb-4">
    <div className="d-flex align-items-center mb-3">
      <span className="me-2 fs-5">{icon}</span>
      <h5 className="m-0">{title}</h5>
    </div>

    {items?.length ? (
      <div className="row g-3">
        {items.map((item, index) => (
          <div className="col-12" key={item.id || index}>
            {renderItem(item)}
          </div>
        ))}
      </div>
    ) : (
      <div className="alert alert-light border d-flex align-items-center" role="alert">
        <span className="me-2 text-muted">No {title.toLowerCase()} found.</span>
      </div>
    )}

    {footer}
  </section>
);

const StatusBadge = ({ status }) => {
  if (!status) return null;
  const map = { pending: "warning", approved: "success", rejected: "danger", verified: "success" };
  const variant = map[status] || "secondary";
  return <div><span className={`badge bg-${variant} text-uppercase`}>{status}</span></div>;
};

const Card = ({ headerIcon, title, subtitle, details, rightSlot, footer }) => (
  <div className="card shadow-sm border-0">
    <div className="card-body">
      <div className="d-flex justify-content-between">
        <div className="d-flex align-items-start">
          <div className="me-3 fs-4">{headerIcon}</div>
          <div>
            <h6 className="card-title mb-1">{title}</h6>
            {subtitle && <div className="text-muted small">{subtitle}</div>}
            {details && <div className="text-muted small mt-1">{details}</div>}
          </div>
        </div>
        {rightSlot}
      </div>
    </div>
    {footer && <div className="card-footer bg-white border-0 pt-0">{footer}</div>}
  </div>
);

// ---------- page ----------
let refetchWorkUpdate;

const WorkUpdate = () => {
  const [workItems, setWorkItems] = useState([]);
  const [workPage, setWorkPage] = useState(1);
  const [workTotalPages, setWorkTotalPages] = useState(1);
  const [workLoading, setWorkLoading] = useState(true);

  const [docItems, setDocItems] = useState([]);
  const [docPage, setDocPage] = useState(1);
  const [docTotalPages, setDocTotalPages] = useState(1);
  const [docsLoading, setDocsLoading] = useState(true);

  const [incomeItems, setIncomeItems] = useState([]);
  const [incomePage, setIncomePage] = useState(1);
  const [incomeTotalPages, setIncomeTotalPages] = useState(1);
  const [incomeLoading, setIncomeLoading] = useState(true);

  const perPage = 5;

  // --- fetchers ---
  const fetchWork = useCallback(async (page = 1) => {
    try {
      setWorkLoading(true);
      const filters = { page, per_page: perPage };
      const res = await getUserWorkExperience(filters);

      const normalized = normalizePaginated(res?.data?.work_experiences);
      setWorkItems(normalized.items);
      setWorkPage(normalized.current);
      setWorkTotalPages(normalized.total);
    } catch (e) {
      // console.error("Work fetch error:", e);
      setWorkItems([]);
      setWorkPage(1);
      setWorkTotalPages(1);
    } finally {
      setWorkLoading(false);
    }
  }, []);

  const fetchDocs = useCallback(async (page = 1) => {
    try {
      setDocsLoading(true);
      const filters = { page, per_page: perPage };
      const res = await getUserDocsUpload(filters);

      const normalized = normalizePaginated(res?.data?.documents);
      setDocItems(normalized.items);
      setDocPage(normalized.current);
      setDocTotalPages(normalized.total);
    } catch (e) {
      // console.error("Docs fetch error:", e);
      setDocItems([]);
      setDocPage(1);
      setDocTotalPages(1);
    } finally {
      setDocsLoading(false);
    }
  }, []);

  const fetchIncome = useCallback(async (page = 1) => {
    try {
      setIncomeLoading(true);
      const filters = { page, per_page: perPage };
      const res = await getUserIncomeSources(filters);

      const src = res?.data?.income_sources;
      const normalized = normalizePaginated(src);
      setIncomeItems(normalized.items);
      setIncomePage(normalized.current);
      setIncomeTotalPages(normalized.total);
    } catch (e) {
      // console.error("Income fetch error:", e);
      setIncomeItems([]);
      setIncomePage(1);
      setIncomeTotalPages(1);
    } finally {
      setIncomeLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWork(1);
    fetchDocs(1);
    fetchIncome(1);
  }, [fetchWork, fetchDocs, fetchIncome]);

  refetchWorkUpdate = () => {
    fetchWork(workPage);
    fetchDocs(workPage);
    fetchIncome(workPage);
  };

  return (
    <div className="work__update__page container px-0">
      {/* Work Experiences */}
      <Section title="Work Experiences" icon={<FaBriefcase className="text-primary" />} items={workItems}
        renderItem={(item) => {
          const dateText = `${formatDate(item.start_date)} — ${item.is_current ? "Present" : formatDate(item.end_date)}`;
          return (
            <Card
              headerIcon={<FaBriefcase className="text-primary" />}
              title={`${item.job_title || "Role"} @ ${item.company_name || "-"}`}
              subtitle={`${titleCaseFromSnake(item.employment_type || "")}${
                item.industry ? ` · ${item.industry}` : ""
              }${item.location ? ` · ${item.location}` : ""}`}
              details={dateText}
              rightSlot={<StatusBadge status={item.verification_status} />}
              footer={
                item.job_description ? (
                  <ul className="list-group list-group-flush">
                    <li className="list-group-item px-0">
                      <span className="fw-semibold">Description: </span>
                      <span className="text-muted">{item.job_description}</span>
                    </li>
                    {item.salary && (
                      <li className="list-group-item px-0">
                        <span className="fw-semibold">Salary: </span>
                        <span className="text-muted">
                          {item.currency?.toUpperCase()} {Number(item.salary).toLocaleString()} (
                          {titleCaseFromSnake(item.salary_frequency)})
                        </span>
                      </li>
                    )}
                    <li className="list-group-item px-0">
                      <span className="fw-semibold">Contact employer: </span>
                      <span className="text-muted">{item.can_contact_employer ? "Yes" : "No"}</span>
                    </li>
                  </ul>
                ) : null
              }
            />
          );
        }}
        footer={
          <div className="mt-3 d-flex justify-content-between align-items-center">
            {workLoading ? (
              <div className="d-flex align-items-center">
                <span className="spinner-border spinner-border-sm me-2" />
                <span>Loading…</span>
              </div>
            ) : (
              <small className="text-muted">Page {workPage} of {workTotalPages}</small>
            )}

            {workTotalPages > 1 && (
              <Pagination
                currentPage={workPage}
                totalPages={workTotalPages}
                onPageChange={(next) => {
                  if (next < 1 || next > workTotalPages) return;
                  fetchWork(next);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            )}
          </div>
        }
      />

      {/* Documents */}
      <Section title="Uploaded Documents" icon={<FaFileAlt className="text-success" />} items={docItems}
        renderItem={(item) => (
          <Card
            headerIcon={<FaFileAlt className="text-success" />}
            title={titleCaseFromSnake(item.document_type)}
            subtitle={item.original_filename}
            details={`Uploaded: ${item.upload_date || formatDate(item.created_at)}`}
            rightSlot={<StatusBadge status={item.status} />}
            footer={
              <div className="d-flex flex-wrap gap-2 small text-muted">
                <span>Type: {item.mime_type}</span>
                {item.file_size_formatted && <span>• Size: {item.file_size_formatted}</span>}
              </div>
            }
          />
        )}
        footer={
          <div className="mt-3 d-flex justify-content-between align-items-center">
            {docsLoading ? (
              <div className="d-flex align-items-center">
                <span className="spinner-border spinner-border-sm me-2" />
                <span>Loading…</span>
              </div>
            ) : (
              <small className="text-muted">Page {docPage} of {docTotalPages}</small>
            )}

            {docTotalPages > 1 && (
              <Pagination
                currentPage={docPage}
                totalPages={docTotalPages}
                onPageChange={(next) => {
                  if (next < 1 || next > docTotalPages) return;
                  fetchDocs(next);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            )}
          </div>
        }
      />

      {/* Income Sources */}
      <Section title="Income Sources" icon={<FaMoneyBillWave className="text-warning" />} items={incomeItems}
        renderItem={(item) => (
          <Card
            headerIcon={<FaMoneyBillWave className="text-warning" />}
            title={item.employer_or_business_name || titleCaseFromSnake(item.source_type)}
            subtitle={`${Number(item.amount).toLocaleString()} ${item.currency?.toUpperCase()} (${titleCaseFromSnake(
              item.frequency
            )})`}
            details={`${formatDate(item.start_date)} — ${item.end_date ? formatDate(item.end_date) : "Present"}`}
          />
        )}
        footer={
          <div className="mt-3 d-flex justify-content-between align-items-center">
            {incomeLoading ? (
              <div className="d-flex align-items-center">
                <span className="spinner-border spinner-border-sm me-2" />
                <span>Loading…</span>
              </div>
            ) : (
              <small className="text-muted">Page {incomePage} of {incomeTotalPages}</small>
            )}

            {/* Only render pager if API returned pagination meta */}
            {incomeTotalPages > 1 && (
              <Pagination
                currentPage={incomePage}
                totalPages={incomeTotalPages}
                onPageChange={(next) => {
                  if (next < 1 || next > incomeTotalPages) return;
                  fetchIncome(next);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            )}
          </div>
        }
      />
    </div>
  );
};

export default WorkUpdate;
export { refetchWorkUpdate };